import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { ArrowLeft, Plus, X } from 'lucide-react';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface VitaminConceptTestFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  onSavePayload?: (payload: Record<string, any>) => void | Promise<void>;
}

interface PopulationOption {
  name: string;
  agent_count: number;
}

const API_URL = import.meta.env.VITE_API_URL || '';

const VitaminConceptTestForm: React.FC<VitaminConceptTestFormProps> = ({
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {},
  onSavePayload,
}) => {
  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [productHeadline, setProductHeadline] = useState(initialFormData.productHeadline || '');
  const [brandName, setBrandName] = useState(initialFormData.brandName || '');
  const [consumerInsight, setConsumerInsight] = useState(initialFormData.consumerInsight || '');
  const [mainBenefit, setMainBenefit] = useState(initialFormData.mainBenefit || '');
  const [reasonsToBelieve, setReasonsToBelieve] = useState<string[]>(
    Array.isArray(initialFormData.reasonsToBelieve)
      ? initialFormData.reasonsToBelieve
      : initialFormData.reasonsToBelieve
        ? [initialFormData.reasonsToBelieve]
        : ['']
  );
  const [pricePoint, setPricePoint] = useState(initialFormData.pricePoint || '');
  const [packSize, setPackSize] = useState(initialFormData.packSize || '');
  const [productImageUrl, setProductImageUrl] = useState(initialFormData.productImageUrl || '');
  const [competitorProducts, setCompetitorProducts] = useState<string[]>(
    Array.isArray(initialFormData.competitorProducts)
      ? initialFormData.competitorProducts
      : initialFormData.competitorProducts
        ? [initialFormData.competitorProducts]
        : ['']
  );
  const [sampleSize, setSampleSize] = useState<number | ''>(initialFormData.sampleSize ?? '');
  const [population, setPopulation] = useState(initialFormData.population || '');
  const [populations, setPopulations] = useState<PopulationOption[]>([]);
  const [populationsLoading, setPopulationsLoading] = useState(true);

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchPopulations = async () => {
      try {
        const resp = await fetch(`${API_URL}/vitamin/populations`, { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          const pops: PopulationOption[] = data.populations || data || [];
          setPopulations(pops);
          if (!population && pops.length > 0) {
            setPopulation(pops[0].name);
          }
        }
      } catch (e) {
        console.error('Failed to fetch populations:', e);
      } finally {
        setPopulationsLoading(false);
      }
    };
    fetchPopulations();
  }, []);

  useEffect(() => {
    onFormDataChange({
      simName,
      productHeadline,
      brandName,
      consumerInsight,
      mainBenefit,
      reasonsToBelieve,
      pricePoint,
      packSize,
      productImageUrl,
      competitorProducts,
      sampleSize,
      population,
    });
  }, [simName, productHeadline, brandName, consumerInsight, mainBenefit, reasonsToBelieve, pricePoint, packSize, productImageUrl, competitorProducts, sampleSize, population]);

  const handleFormChange = () => {
    if (onEditStep) onEditStep();
  };

  const isFormValid =
    productHeadline.trim() !== '' &&
    brandName.trim() !== '' &&
    mainBenefit.trim() !== '' &&
    population !== '';

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const rtbFiltered = reasonsToBelieve.map(s => s.trim()).filter(Boolean);
      const compFiltered = competitorProducts.map(s => s.trim()).filter(Boolean);

      const payload: Record<string, any> = {
        task: 'vitamin-concept-test',
        name: simName || `${brandName} — Concept Test`,
        product_headline: productHeadline,
        brand_name: brandName,
        consumer_insight: consumerInsight,
        main_benefit: mainBenefit,
        reasons_to_believe: rtbFiltered,
        price_point: pricePoint,
        pack_size: packSize,
        product_image_url: productImageUrl || undefined,
        competitor_products: compFiltered.length > 0 ? compFiltered : undefined,
        population,
        sample_size: sampleSize === '' ? null : sampleSize,
      };

      if (onSavePayload) {
        await onSavePayload(payload);
      }
    } catch (err) {
      console.error('Error in form submission:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectClasses =
    'block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white';
  const inputClasses =
    'block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500';

  const updateRtb = (idx: number, val: string) => {
    setReasonsToBelieve(prev => { const c = [...prev]; c[idx] = val; return c; });
    handleFormChange();
  };
  const addRtb = () => { setReasonsToBelieve(prev => [...prev, '']); handleFormChange(); };
  const removeRtb = (idx: number) => {
    setReasonsToBelieve(prev => prev.length <= 1 ? [''] : prev.filter((_, i) => i !== idx));
    handleFormChange();
  };

  const updateComp = (idx: number, val: string) => {
    setCompetitorProducts(prev => { const c = [...prev]; c[idx] = val; return c; });
    handleFormChange();
  };
  const addComp = () => { setCompetitorProducts(prev => [...prev, '']); handleFormChange(); };
  const removeComp = (idx: number) => {
    setCompetitorProducts(prev => prev.length <= 1 ? [''] : prev.filter((_, i) => i !== idx));
    handleFormChange();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to use case selection
          </Button>
        </div>

        {error && (
          <div className="text-sm text-red-600 mb-3 bg-red-50 p-3 rounded-md">
            {error}
          </div>
        )}
      </div>

      {/* Simulation Name */}
      <div className="mb-6">
        <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
          Simulation Name (Optional)
        </label>
        <input
          id="simName"
          type="text"
          className={inputClasses}
          value={simName}
          onChange={(e) => { setSimName(e.target.value); handleFormChange(); }}
          placeholder="e.g., CalmKids Gummies — Wave 1"
        />
      </div>

      {/* Product Headline */}
      <div className="mb-6">
        <label htmlFor="productHeadline" className="block text-sm font-medium text-gray-700 mb-2">
          Product Headline <span className="text-red-500">*</span>
        </label>
        <input
          id="productHeadline"
          type="text"
          className={inputClasses}
          value={productHeadline}
          onChange={(e) => { setProductHeadline(e.target.value); handleFormChange(); }}
          placeholder="e.g., Daily Calm & Focus Gummies for Teens"
        />
      </div>

      {/* Brand Name */}
      <div className="mb-6">
        <label htmlFor="brandName" className="block text-sm font-medium text-gray-700 mb-2">
          Brand Name <span className="text-red-500">*</span>
        </label>
        <input
          id="brandName"
          type="text"
          className={inputClasses}
          value={brandName}
          onChange={(e) => { setBrandName(e.target.value); handleFormChange(); }}
          placeholder="e.g., CalmKids"
        />
      </div>

      {/* Consumer Insight */}
      <div className="mb-6">
        <label htmlFor="consumerInsight" className="block text-sm font-medium text-gray-700 mb-2">
          Consumer Insight
        </label>
        <textarea
          id="consumerInsight"
          rows={2}
          className={inputClasses}
          value={consumerInsight}
          onChange={(e) => { setConsumerInsight(e.target.value); handleFormChange(); }}
          placeholder="e.g., Teens need natural stress support during school and hormonal changes"
        />
      </div>

      {/* Main Benefit */}
      <div className="mb-6">
        <label htmlFor="mainBenefit" className="block text-sm font-medium text-gray-700 mb-2">
          Main Benefit <span className="text-red-500">*</span>
        </label>
        <textarea
          id="mainBenefit"
          rows={2}
          className={inputClasses}
          value={mainBenefit}
          onChange={(e) => { setMainBenefit(e.target.value); handleFormChange(); }}
          placeholder="e.g., Calm focus without drowsiness"
        />
      </div>

      {/* Reasons to Believe (array) */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Reasons to Believe
        </label>
        <div className="space-y-2">
          {reasonsToBelieve.map((rtb, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="text"
                className={inputClasses + ' flex-1'}
                value={rtb}
                onChange={(e) => updateRtb(idx, e.target.value)}
                placeholder={`e.g., L-Theanine (200mg) for calm focus`}
              />
              <button
                type="button"
                onClick={() => removeRtb(idx)}
                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addRtb}
          className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus className="w-3.5 h-3.5" /> Add reason
        </button>
      </div>

      {/* Price Point & Pack Size */}
      <div className="mb-6 grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="pricePoint" className="block text-sm font-medium text-gray-700 mb-2">
            Price Point
          </label>
          <input
            id="pricePoint"
            type="text"
            className={inputClasses}
            value={pricePoint}
            onChange={(e) => { setPricePoint(e.target.value); handleFormChange(); }}
            placeholder="e.g., $27.99 ($24.99 Subscribe & Save)"
          />
        </div>
        <div>
          <label htmlFor="packSize" className="block text-sm font-medium text-gray-700 mb-2">
            Pack Size
          </label>
          <input
            id="packSize"
            type="text"
            className={inputClasses}
            value={packSize}
            onChange={(e) => { setPackSize(e.target.value); handleFormChange(); }}
            placeholder="e.g., 60 gummies / 30-day supply"
          />
        </div>
      </div>

      {/* Product Image URL */}
      <div className="mb-6">
        <label htmlFor="productImageUrl" className="block text-sm font-medium text-gray-700 mb-2">
          Product Image URL
        </label>
        <input
          id="productImageUrl"
          type="url"
          className={inputClasses}
          value={productImageUrl}
          onChange={(e) => { setProductImageUrl(e.target.value); handleFormChange(); }}
          placeholder="e.g., https://example.com/product-image.jpg"
        />
      </div>

      {/* Competitor Products (array) */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Competitor Products
        </label>
        <div className="space-y-2">
          {competitorProducts.map((cp, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="text"
                className={inputClasses + ' flex-1'}
                value={cp}
                onChange={(e) => updateComp(idx, e.target.value)}
                placeholder={`e.g., OLLY Teen Multi`}
              />
              <button
                type="button"
                onClick={() => removeComp(idx)}
                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addComp}
          className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
        >
          <Plus className="w-3.5 h-3.5" /> Add competitor
        </button>
      </div>

      {/* Sample Size (optional) */}
      <div className="mb-6">
        <label htmlFor="sampleSize" className="block text-sm font-medium text-gray-700 mb-2">
          Number of Agents
          <span className="text-gray-400 font-normal ml-2">(optional, 1-100)</span>
        </label>
        <input
          id="sampleSize"
          type="number"
          min={1}
          max={100}
          className={inputClasses}
          value={sampleSize}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') { setSampleSize(''); handleFormChange(); return; }
            const val = parseInt(raw, 10);
            if (!isNaN(val)) setSampleSize(Math.min(100, Math.max(1, val)));
            handleFormChange();
          }}
          onBlur={() => {
            if (sampleSize !== '' && sampleSize < 1) setSampleSize(1);
          }}
          placeholder="Leave blank for API default"
        />
      </div>

      {/* Population */}
      <div className="mb-6">
        <label htmlFor="population" className="block text-sm font-medium text-gray-700 mb-2">
          Population <span className="text-red-500">*</span>
        </label>
        {populationsLoading ? (
          <div className="text-sm text-gray-400 py-2">Loading populations...</div>
        ) : populations.length > 0 ? (
          <select
            id="population"
            className={selectClasses}
            value={population}
            onChange={(e) => { setPopulation(e.target.value); handleFormChange(); }}
          >
            {populations.map(p => (
              <option key={p.name} value={p.name}>
                {p.name.replace(/_/g, ' ')} ({p.agent_count} agents)
              </option>
            ))}
          </select>
        ) : (
          <input
            id="population"
            type="text"
            className={inputClasses}
            value={population}
            onChange={(e) => { setPopulation(e.target.value); handleFormChange(); }}
            placeholder="e.g., population_vitamins"
          />
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
          withArrow
        >
          {isSubmitting ? 'Processing...' : onSavePayload ? 'Continue' : 'Run Vitamin Concept Test'}
        </Button>
      </div>
    </div>
  );
};

export default VitaminConceptTestForm;
